import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiGet } from "../../../api/apiGet";
import { apiPut } from "../../../api/apiPut";
import { PREPAID_WEIGHT } from "../../../helpers/const";
import { apiPost } from "../../../api/apiPost";
import { getOrderListByParameters } from "../OrderSlice/OrderSlice";
import { extractWarehouseInfo } from "../../../helpers/helperFunctions";

export const getCustomerWarehouseCutoff = createAsyncThunk(
  "customerSlice/getCustomerWarehouseCutoff",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const {
        orders: {
          selectedReduxRate,
          shippingInfo: { preferred_rate, combinations } = {},
        },
      } = state;

      // Extract warehouse keys using the same logic as createOrder
      const { warehouseKeys } = extractWarehouseInfo(
        selectedReduxRate,
        preferred_rate,
        combinations
      );

      const response = await apiGet.getCustomerWarehouseCutoff({
        warehouses: warehouseKeys,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getQuoteListByParameters = createAsyncThunk(
  "subOrder/getQuoteListByParameters",
  async (
    { quoteId, page, customerNum, customerName, orderBy, sortOrder, from, to },
    thunkAPI
  ) => {
    try {
      const resp = await apiGet.getQuotesList({
        quoteId,
        page,
        customerNum,
        customerName,
        orderBy,
        sortOrder,
        from,
        to,
      });
      return resp;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error getting the list of quotes.",
      });
    }
  }
);

export const getQuoteById = createAsyncThunk(
  "quote/get",
  async (id, thunkAPI) => {
    try {
      const response = await apiGet.getQuoteById(id);
      return {
        quote: response.data,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error getting the credit.",
      });
    }
  }
);

export const getInvoicesByFilter = createAsyncThunk(
  "invoice/getInvoicesByFilter",
  async (
    {
      invoice_num,
      customer_num,
      ref_number,
      customer_po,
      search,
      to,
      from,
      page,
      order_by,
      sort_order,
    },
    thunkAPI
  ) => {
    try {
      const response = await apiGet.getInvoicesByFilter({
        invoice_num,
        customer_num,
        ref_number,
        customer_po,
        search,
        to,
        from,
        page,
        order_by,
        sort_order,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error loading the invoices.",
      });
    }
  }
);

export const getCustomerDetailsForOrder = createAsyncThunk(
  "customerSlice/getCustomerDetailsForOrder",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getCustomerDetailsForOrder();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getCustomerLoginAs = createAsyncThunk(
  "customerSlice/getCustomerLoginAs",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getCustomerLoginAs();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const updateLoginAsCustomer = createAsyncThunk(
  "customerSlice/updateLoginAsCustomer",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const {
        customer: { newCustomerId },
      } = state;
      await apiPut.updateLoginCustomer({
        customer_num: newCustomerId.id,
      });
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const validateAddress = createAsyncThunk(
  "customer/validateAddress",
  async (_, thunkAPI) => {
    try {
      const storeStates = thunkAPI.getState();
      const {
        customer: {
          customer: { shipping_address: dropShipAddress },
        },
      } = storeStates;

      const response = await apiPost.validateAddress({
        address_line_1: dropShipAddress.street,
        address_line_2: dropShipAddress.street_two,
        city: dropShipAddress.city,
        state: dropShipAddress.state,
        zip_code: dropShipAddress.zip_code,
        country: "US",
      });

      return response.data;
    } catch (error) {}
  }
);

// If we ever do more with credits this will be moved to its own slice

export const getCreditById = createAsyncThunk(
  "credit/get",
  async (id, thunkAPI) => {
    try {
      const response = await apiGet.getCredit(id);
      return {
        credit: response.data,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error getting the credit.",
      });
    }
  }
);

export const sendCreditConfirmation = createAsyncThunk(
  "credit/sendCreditConfrimation",
  async ({ email, method, credit_memo_num, use_rga }, thunkAPI) => {
    try {
      const response = await apiGet.sendCreditConfirmation({
        email,
        method,
        credit_memo_num,
        use_rga,
      });
      if (response && response?.data?.type !== "application/json") {
        const blob = response.data;

        const fileURL = window.URL.createObjectURL(blob);

        const printWindow = window.open(fileURL, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.focus();
          };
        } else {
          alert("Popup blocked. Please allow popups to print.");

          const link = document.createElement("a");
          link.href = fileURL;

          const disposition = response.headers["content-disposition"];
          const match = disposition?.match(/filename="?([^"]+)"?/);
          const filename = match ? match[1] : "download.pdf";

          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileURL);
        }
        return { result: 1 };
      }
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error sending the confirmation.",
      });
    }
  }
);

export const getCreditsByFilter = createAsyncThunk(
  "credit/creditsByFilter",
  async (
    {
      credit_memo_num,
      ref_invoice,
      return_address,
      to,
      from,
      status,
      selected,
      customer_num,
      page,
      order_by,
      sort_order,
    },
    thunkAPI
  ) => {
    try {
      const response = await apiGet.getCreditsByFilter({
        credit_memo_num,
        ref_invoice,
        return_address,
        customer_num,
        to,
        from,
        status,
        selected,
        page,
        order_by,
        sort_order,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error loading the credits or no more credits available.",
      });
    }
  }
);

export const getProductHistory = createAsyncThunk(
  "customer/productHistory",
  async ({ search, sort_by, page }, thunkAPI) => {
    try {
      const response = await apiGet.getProductHistory({
        search,
        sort_by,
        page,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error loading product history.",
      });
    }
  }
);

export const customerSlice = createSlice({
  name: "customerSlice",
  initialState: {
    customerWarehouseCutoff: [],
    currentCustomerId: 0,
    newCustomerId: 0,
    loginOptions: [],
    pending: false,
    prepaidWeight: PREPAID_WEIGHT,
    customer: {},
    originalShippingAddress: {},
    canChangeShippingAddress: false,
    isDropShip: false,
    customerPo: "",
    validateAddress: undefined,
    validating: false,
    showAddressValidation: false,
    showLoader: false,
    address_classification: 1, // Default commercial
  },
  reducers: {
    setShowAddressValidation: (state, action) => {
      state.showAddressValidation = action.payload;
    },
    setCustomerPo: (state, action) => {
      state.customerPo = action.payload;
    },
    setNewCustomerId: (state, action) => {
      state.newCustomerId = action.payload;
    },
    setShippingAddress: (state, action) => {
      state.customer = {
        ...state.customer,
        shipping_address: { ...action.payload.shippingAddress },
      };
      state.isDropShip = action.payload.isDropShip;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateAddress.pending, (state, action) => {
        state.validating = true;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        state.validating = false;
        state.validateAddress = action.payload;
        state.showAddressValidation = true;
        state.address_classification = parseInt(
          action.payload.address_classification?.Code || 0
        );
      })
      .addCase(getCreditsByFilter.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(getCreditsByFilter.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(getCreditsByFilter.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(getOrderListByParameters.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(getOrderListByParameters.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(getOrderListByParameters.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(validateAddress.rejected, (state) => {
        state.validating = false;
      })
      .addCase(getInvoicesByFilter.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(getInvoicesByFilter.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(getInvoicesByFilter.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(getQuoteListByParameters.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(getQuoteListByParameters.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(getQuoteListByParameters.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(getProductHistory.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(getProductHistory.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(getProductHistory.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(updateLoginAsCustomer.pending, (state, action) => {
        state.pending = true;
      })
      .addCase(updateLoginAsCustomer.fulfilled, (state, action) => {
        state.pending = false;
      })
      .addCase(updateLoginAsCustomer.rejected, (state, action) => {
        state.pending = false;
      })
      .addCase(getCustomerDetailsForOrder.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          originalShippingAddress: action.payload.customer?.shipping_address,
          isDropShip: false,
        };
      })
      .addCase(getCustomerLoginAs.fulfilled, (state, action) => {
        state.loginOptions = action.payload.customer_list;
        state.currentCustomerId = action.payload.logged_in_as;
        state.newCustomerId = action.payload.logged_in_as;
      })
      .addCase(getCustomerWarehouseCutoff.fulfilled, (state, action) => {
        const { free_freight, ...cutoff } = action.payload;
        state.customerWarehouseCutoff = cutoff;
        state.prepaidWeight = free_freight;
      });
  },
});

export const {
  setNewCustomerId,
  setShippingAddress,
  setCustomerPo,
  setShowAddressValidation,
} = customerSlice.actions;

export default customerSlice.reducer;
